import React, { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useGetDriversMutation } from "../../../store/api/lumi/drivers";
import { getType } from "../../../utils/date";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { Empty, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetConceptsOptionsMutation } from "../../../store/api/lumi/concepts";
import {
    CartesianGrid,
    LabelList,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useSelector } from "react-redux";

export function ScoreDriversWidget(props) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [baseline, setBaseline] = useState(0);
    const [yDomain, setYDomain] = useState([0, 10]); // Default domain

    const [getConceptOptionMut] = useGetConceptsOptionsMutation();
    const [getDriver] = useGetDriversMutation();

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });

    async function getConceptsOptions() {
        let conceptFilter = { type: "top", limit: 100 };
        const { data: dataConcepts } = await getConceptOptionMut({
            id: props.id,
            concept_selector: conceptFilter,
            filter: props.filters,
        });
        props.setFilterDocCount(dataConcepts.filter_count);
    }

    async function getDriversData() {
        setIsLoading(true);
        const conceptFilter = getConceptFilter(props);

        const { data: dataConcepts } = await getDriver({
            id: props.id,
            score_field: props.selectedFieldDriverName,
            concept_selector: conceptFilter,
            filter: props.filters,
            match_type: getType(props.seletctedType),
        });

        const baseline = dataConcepts.length > 0 ? dataConcepts[0].baseline.toFixed(3) : 0;
        const processedData = dataConcepts.map(({ name, match_count, average_score }) => ({
            name,
            match_count,
            average_score: average_score.toFixed(3),
        }));

        setBaseline(baseline);
        setData(processedData);

        const scores = processedData.map((d) => parseFloat(d.average_score));
        const minY = Math.min(...scores, baseline);
        const maxY = Math.max(...scores, baseline);
        const padding = (maxY - minY) * 0.1;
        setYDomain([minY - padding, maxY + padding]);

        setIsLoading(false);
    }


    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const averageScore = payload[0].payload.average_score;
            const difference = (averageScore - baseline).toFixed(3);
            const formattedDifference = difference > 0 ? `+${difference}` : difference;

            return (
                <div className="custom-tooltip"
                     style={{ backgroundColor: "#ffffff", padding: "10px", border: "1px solid #cccccc" }}>
                    <p><strong>{payload[0].payload.name}</strong></p>
                    <p>Total Matches: {payload[0].payload.match_count}</p>
                    <p>Average Score: {averageScore}</p>
                    <p>Difference from overall score: {formattedDifference}</p>
                </div>
            );
        }
        return null;
    };

    const splitText = (text, maxLineLength) => {
        const words = text.split(" ");
        const lines = [];
        let currentLine = "";

        words.forEach(word => {
            if ((currentLine + word).length > maxLineLength) {
                lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine += currentLine ? ` ${word}` : word;
            }
        });

        if (currentLine) lines.push(currentLine);
        return lines.join("\n");
    };

    useEffect(() => {
        setIsLoading(true);
        if (props.selectedFieldDriverName) {
            const debouncedGetDrivers = debounce(getDriversData, 500);
            const debouncedGetConcepts = debounce(getConceptsOptions, 500);

            debouncedGetDrivers();
            debouncedGetConcepts();

            return () => {
                debouncedGetDrivers.cancel();
                debouncedGetConcepts.cancel();
            };
        }
    }, [
        props.seletctedType,
        props.conceptTypeDay,
        props.conceptList,
        props.selectedFieldDriverName,
        props.filters,
        props.seletctedType,
        props.selectedPageSize,
    ]);


    if (isLoading) {
        return <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />;
    }
    if (data.length === 0) {
        return <Empty style={{ position: "absolute", top: "45%", right: "33%" }} />;
    }
    return (
        <div className="volume-compare" style={{ height: props.widget.layout.h * 39 - 150 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, left: 50, bottom: 50 }}>
                    <CartesianGrid />
                    <XAxis
                        type="number"
                        dataKey="match_count"
                        name="Total Matches"
                        tick={{ style: { fontWeight: "bold", fontSize: 16 } }}
                        label={{ value: "Total Matches", position: "insideBottom", offset: -20, fontSize: 16 }}
                    />
                    <YAxis
                        type="number"
                        dataKey="average_score"
                        name="Average Score"
                        label={{
                            value: splitText(`Average Score (${props.selectedFieldDriverName})`, 15),
                            angle: -90,
                            dx:-40,
                            position: "insideLeft",
                            style: { textAnchor: "middle", fontWeight: "bold", fontSize: 16 },
                        }}
                        fill={"black"}
                        domain={yDomain}
                        tick={{ style: { fontWeight: "bold", fontSize: 16 } }}
                        tickFormatter={(tick) => (Math.round(tick * 200) / 200).toFixed(3)}


                    />
                    <YAxis
                        type="number"
                        dataKey="average_score"
                        orientation="right"
                        label={{
                            value: `Overall average score ${baseline}`,
                            angle: 90,
                            position: "insideRight",
                            style: { textAnchor: "middle", fontWeight: "bold", fontSize: 16 },
                        }}
                        domain={yDomain}
                        tick={false}
                        axisLine={false}
                        yAxisId="right"
                    />


                    <ReferenceLine
                        y={baseline}
                        stroke="black"
                        strokeWidth={3}
                        strokeDasharray="15 10"
                        label={{
                            position: "right",
                            fontSize: 16,
                            fontWeight: "bold",
                            fill: "black",
                        }}
                    />


                    <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomTooltip />} />

                    <Scatter
                        name="Above Baseline"
                        data={data.filter((d) => d.average_score >= baseline)}
                        fill="#3ebde6"
                    >
                        <LabelList dataKey="name" position="top" fill="black"
                                   style={{ fontSize: 16, color: "black" }} />
                    </Scatter>

                    <Scatter
                        name="Below Baseline"
                        data={data.filter((d) => d.average_score < baseline)}
                        fill="#ef6867"
                    >
                        <LabelList dataKey="name" position="top" fill="black"
                                   style={{ fontSize: 16, color: "black" }} />
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
}
